import { useState, useMemo, useCallback, useEffect } from 'react';
import type { UpdateBiddingDataRequest } from '../../../types';
import { CombinedCategoryOptions, ListingCategoryConst, parseCombinedCategory, PrefTypeConst } from '../../../constants';
import { useAuth } from '../../../contexts/AuthContext';
import { useItemFilters } from '../../../contexts/ItemFilterContext'; // フィルターContext
import { useMasters } from '../../../contexts/MastersContext'; // マスタContext
import { useBiddingInfoGetInfoListForBuyerQuery, useUpsertBidMutation } from '../../../hooks/useBiddingInfoQuery';
import { BiddingInputFilterDialog } from '../../../components/buyer/BiddingInput/BiddingInputFilterDialog'; // フィルターダイアログ
import { BiddingInputItemRow } from '../../../components/buyer/BiddingInput/BiddingInputItemRow'; // 入札用のItemRow
import BiddingInputNumberPage from './BiddingInputNumberPage';
import { BiddingItemStatusConst } from '../../../constants';
import { useGetBiddingDateForApplyQuery } from '../../../hooks/useCalendarQuery';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useGetFavoritesQuery } from '../../../hooks/useBiddingItemsQuery';
import { useBiddingConfigQuery } from '../../../hooks/useSystemMasterQuery';
import { useAppDialog } from '../../../hooks/useAppDialog';

dayjs.extend(utc);
dayjs.extend(timezone);

// デフォルトのタイムゾーンを日本時間(JST)に設定
dayjs.tz.setDefault("Asia/Tokyo");

// # メインのページコンポーネント ---
const BiddingInputPage = () => {
  //----------------
  // Context使用定義
  //----------------
  const { currentUser } = useAuth();  // 認証context
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters(); // 検索条件context
  const { 
    isMastersLoading, 
    sellers, farmers, teaTypes, teaSeasons, varieties, gaps 
  } = useMasters(); // マスタcontex
  const { data: initialBiddingDate, isLoading: isDateLoading } = useGetBiddingDateForApplyQuery(currentUser?.loginId); 
  const { data: config } = useBiddingConfigQuery();　//最低落札値警告関連設定取得

  // 共通ダイアログ用フック
  const { showConfirm, OverlayElement } = useAppDialog();
  
  const [modalBiddingNumber, setModalBiddingNumber] = useState<string | null>(null);

  //----------------
  // State定義
  //----------------
  const [isFilterOpen, setIsFilterOpen] = useState(false);  // 検索ダイアログ表示の状態管理(true:検索ダイアログ表示中)
  const [selectedKey, setSelectedKey] = useState<string | null>(null); // 選択中の明細("itemId_branchNumber" 形式)
  const [isInitialized, setIsInitialized] = useState(false); // 追加: 初期化完了フラグ
  const [displayMode, setDisplayMode] = useState<'all' | 'favorites'>('all');//お気に入り絞り込む用
  //----------------
  // Memo定義
  //----------------
  const masterDataForComponents = useMemo(() => ({
    varieties, teaTypes, teaSeasons, sellers, farmers, gaps
  }), [varieties, teaTypes, teaSeasons, sellers, farmers, gaps]); // マスタcontextから取得した各マスタデータ


  //----------------
  // Effect定義 (初期化処理)
  //----------------
  useEffect(() => {
    // 1. データの読み込み待機
    if (isDateLoading || isMastersLoading || !currentUser) return;
    if (isInitialized) return; // 二重実行防止

    const newFilters = { ...itemFilters };
    let hasChanged = false;

    // A. 入札日の初期設定 (APIからの日付 or 今日)
    if (!newFilters.biddingDate) {
      newFilters.biddingDate = initialBiddingDate ? String(initialBiddingDate) : dayjs().tz().format("YYYYMMDD");
      hasChanged = true;
    }

    // B. 出品区分の初期設定 (通常入札)
    if (!newFilters.listingCategory) {
      newFilters.listingCategory = ListingCategoryConst.BIDDING;
      hasChanged = true;
    }

    // C. 県内/県外区分の初期設定 (県内)
    if (!newFilters.prefectureType) {
      newFilters.prefectureType = PrefTypeConst.INTERNAL;
      hasChanged = true;
    }

    // 変更があればContext(URL)を更新
    if (hasChanged) {
      updateFiltersAndNavigate(newFilters);
    }

    setIsInitialized(true); // 初期化完了
  }, [initialBiddingDate, isDateLoading, isMastersLoading, currentUser, itemFilters, isInitialized, updateFiltersAndNavigate]);

  //----------------
  // Hooks使用定義
  //----------------
  // 0(ALL)を考慮しなくて良くなるので、スッキリ戻せます
  const isSearchEnabled = !!itemFilters.biddingDate && !!itemFilters.listingCategory;

  const {
    data: responseData,
    isLoading: isItemsLoading, 
    error,
  } = useBiddingInfoGetInfoListForBuyerQuery(
    itemFilters, 
    currentUser?.loginId ?? "",
    { enabled: isInitialized && isSearchEnabled } 
  );

  const { data: favorites } = useGetFavoritesQuery(currentUser?.loginId ?? "");//お気に入り一覧取得用
  
  const upsertBidMutation = useUpsertBidMutation(); // 入札送信用のミューテーションを定義

  const validItems = useMemo(() => {
    // 箱がない、または箱の中に items がない場合は空っぽを返す
    if (!responseData || !responseData.items) return [];
    // 1. 確定済のアイテムのみに絞り込む
    let items = responseData.items.filter((item: any) => item.status === BiddingItemStatusConst.CONFIRMED);

    //  「お気に入りのみ」モードならフィルタリング
    if (displayMode === 'favorites') {
      if (!favorites) return [];
      items = items.filter(item => 
        favorites.some(f => f.itemId === item.itemId && f.branchNumber === item.branchNumber)
      );
    }

    return items;
  }, [responseData, displayMode, favorites]);

  //----------------
  // CallBack関数定義
  //----------------

  // // 選択行を切り替えるハンドラ
  // const handleToggleSelectRow = useCallback((itemId: string, branchNumber: number) => {
  //   const key = `${itemId}_${branchNumber}`;
  //   setSelectedKey(prevKey => (prevKey === key ? null : key)); // 同じ行なら閉じ、違う行なら開く
  // }, []);

  // 入札日フィルター変更ハンドラ
  const handleBiddingDateChange = useCallback((dateValue: string) => {
    const newDate = dateValue.replace(/-/g, ""); // "20260305"
    updateFiltersAndNavigate({
      ...itemFilters,
      biddingDate: newDate,
    });
  }, [itemFilters, updateFiltersAndNavigate]);

  // 出品区分（通常/手合 + 県内/県外）変更ハンドラ
  const handleCombinedCategoryChange = useCallback((selectedValue: string) => {

  const { listingCategory, prefCategory } = parseCombinedCategory(selectedValue);
  
  updateFiltersAndNavigate({
    ...itemFilters,
    listingCategory: listingCategory,
    prefectureType: prefCategory, 
  });
  }, [itemFilters, updateFiltersAndNavigate]);

  // 入札フォームが送信されたときのハンドラ
  const handleSubmitBid = (
    bidData: { 
      itemId: string, 
      branchNumber: number, 
      bidQuantity1: number, 
      bidQuantity2: number, 
      bidPrice: number 
    }
  ) => {
    const inputPrice = bidData.bidPrice;
    const targetItem = validItems.find(i => i.itemId === bidData.itemId && i.branchNumber === bidData.branchNumber);
    const minPrice = targetItem?.bidMinPrice || 0;
    const multiplier = config?.highBidWarningMultiplier ?? 0;

    // --- ① 判定ロジック ---
    
    // A. 最低落札値以下入札禁止設定の場合
    if (inputPrice < minPrice && config?.prohibitBelowMinimumBid) {
      showConfirm({
        title: "一覧から入札",
        message: "入力金額が最低落札値未満のため入札できません、金額を確認してください。",
        onConfirm: () => {}
      });
      return;
    }

    let confirmMessage = "入札を確定しますか？";

    if (inputPrice < minPrice) {
      // B. 最低価格未満
      confirmMessage = "入力金額が最低落札値未満ですが、\n入札を確定してよろしいですか？（入札対象にはなりません）";
    } else if (multiplier > 0 && inputPrice >= minPrice * multiplier) {
      // C. 高額警告
      confirmMessage = `金額が最低落札値の ${multiplier} 倍以上になっています。\n入札を確定してよろしいですか？`;;
    }

    // --- ② ダイアログを出す ---
    showConfirm({
      title: "一覧から入札",
      message: confirmMessage,
      onConfirm: () => {
        // --- ③ 送信処理 ---
        const requestPayload: UpdateBiddingDataRequest = {
          quantity1: bidData.bidQuantity1,
          quantity2: bidData.bidQuantity2,
          bidPrice: inputPrice,
        };

        upsertBidMutation.mutate({
          itemId: bidData.itemId,
          branchNumber: bidData.branchNumber,
          requests: requestPayload 
        }, 
        {
          onSuccess: () => {
            setSelectedKey(null);
          },
        });
      }
    });
  };

  //----------------
  // その他定数定義
  //----------------
  const isPageLoading = isMastersLoading || isDateLoading || !isInitialized || (isItemsLoading && !responseData);

  //----------------
  // レンダリング処理
  //----------------
if (isPageLoading) {
    return ( 
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500 font-bold">読み込み中...</p>
      </div> 
    );
  }
  if (error) {
    return ( 
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-red-100 text-red-700 p-4 rounded-md">エラー: {error.message}</div>
      </div> 
    );
  }

  // メイン描画
  return (
  <div className="bg-white min-h-screen font-sans relative">
      {/* 共通背景レイヤー */}
      {OverlayElement}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">        
        {/* --- ヘッダーエリア --- */}
        <header className="mb-8">
          {/* 上段: タイトルと詳細検索ボタン */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-extrabold text-slate-800">一覧から入札</h1>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition border border-indigo-200"
            >
              詳細検索
            </button>
          </div>
          
          {/* 下段: ヘッダー検索エリア (グレーのボックス & 横並びレイアウト) */}
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 grid grid-cols-2 gap-4 items-start sm:flex sm:gap-6"> 
            
            {/* 1. 入札日 */}
            <div className="w-full sm:w-auto">
              <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1">入札日</label>
              <input 
                type="date" 
                className="w-full sm:w-48 p-2 h-[42px] border border-slate-300 rounded-md bg-white focus:ring-indigo-500 focus:border-indigo-500 appearance-none safari-date-fix"
                value={
                  itemFilters.biddingDate 
                    ? dayjs(String(itemFilters.biddingDate).replace(/^(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3")).format("YYYY-MM-DD") 
                    : ""
                }
                onChange={(e) => handleBiddingDateChange(e.target.value)}
              />
            </div>
              
            {/* 2. 出品区分 */}
            <div className="w-full sm:w-auto">
              <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1">出品区分（県内/県外）</label>
              <select
                value={
                  itemFilters.listingCategory && itemFilters.prefectureType
                    ? `${itemFilters.listingCategory}_${itemFilters.prefectureType}`
                    : ""
                }
                onChange={(e) => handleCombinedCategoryChange(e.target.value)}
                className="w-full sm:w-56 p-2 h-[42px] border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              >
                {CombinedCategoryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
              
            {/* 3. 表示対象 */}
            <div className="col-span-2 sm:col-span-1 w-full sm:w-56">
              <label className="block text-sm font-bold text-slate-700 mb-1">表示対象</label>
              <div className="w-full flex items-center justify-around px-2 bg-white h-[42px] rounded-md border border-slate-300 shadow-sm">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="displayMode"
                    value="all"
                    checked={displayMode === 'all'}
                    onChange={() => setDisplayMode('all')}
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                  <span className="text-sm font-medium text-slate-700 whitespace-nowrap">すべて</span>
                </label>
              
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="displayMode"
                    value="favorites"
                    checked={displayMode === 'favorites'}
                    onChange={() => setDisplayMode('favorites')}
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                  <span className="text-sm font-medium text-slate-700 whitespace-nowrap">お気に入り</span>
                </label>
              </div>
            </div>
          </div>
        </header>
       <main>
          {!isSearchEnabled ? (
            <div className="text-center py-10 text-gray-500">
              入札日と出品区分を選択してください。
            </div>
          ) : (isItemsLoading && !responseData) ? (
            <div className="text-center py-10 text-gray-500">
              読み込み中...
            </div>
          ) : (responseData && !responseData.isSuccess) ? (
            <div className="text-center py-10 text-gray-500">
              {responseData.message}
            </div>
          ) : (validItems.length === 0) ? (
            <div className="text-center py-10 text-gray-500">
               表示する出品情報がありません。
            </div>
          ) : (
            <div className="space-y-4">
              {validItems.map(item => {
                const rowKey = `${item.itemId}_${item.branchNumber}`;
                
                // お気に入り判定をここで行い、Rowに渡す
                const isFavorite = favorites?.some(
                  (f) => f.itemId === item.itemId && f.branchNumber === item.branchNumber
                ) ?? false;

                return (
                  <BiddingInputItemRow 
                    key={rowKey} 
                    item={item} 
                    isSelected={selectedKey === rowKey} 
                    // onToggleSelect={() => handleToggleSelectRow(item.itemId, item.branchNumber)}
                    onClickRow={() => setModalBiddingNumber(item.biddingNumber)}
                    isSaving={upsertBidMutation.isPending}
                    onSubmitBid={handleSubmitBid}
                    listingCategory={item.listingCategory}
                    isFavorite={isFavorite} // ★ Propを追加
                  /> 
                );
              })}
            </div>
          )}
        </main>
        
        <BiddingInputFilterDialog 
          isOpen={isFilterOpen} 
          onClose={() => setIsFilterOpen(false)} 
          masterData={masterDataForComponents}
          isSeller = {false}
          isFarmer = {false}
        />
        
        {modalBiddingNumber && (
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-slate-200 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 duration-300">
              {/* ★ ここで、かんたん入札コンポーネントをダイアログモードで呼び出す */}
              <BiddingInputNumberPage 
                isModal={true} 
                initialBiddingNumber={modalBiddingNumber} 
                onClose={() => setModalBiddingNumber(null)} 
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BiddingInputPage;